import { SellTicketModelType } from '@teinor/erp/types/company/order/sell/sellTicket'
import { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { DisplayPriceGettingParams } from '../../../baseComponents/Displays/DisplayPrice'
import { TwinTableLink } from '../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderDate, CustomRenderDateHours, CustomRenderString } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { OnRowClick } from '../../../baseComponents/TwinTable/VirtualTableListing/types'
import { addDictionaryExtraLine } from '../../../utils/globals/dictionary'
import { sortEmployeesByUser } from '../../../utils/globals/employees'
import locations from '../../../utils/globals/locations'
import withLoading from '../../../utils/hoc/withLoading'
import useSellTicketStatusType from '../../../utils/hooks/sells/orders/useSellTicketStatus'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../utils/reducers'
import { EmployeesPayload } from '../../../utils/reducers/company/employees'
import { getEmployees } from '../../../utils/reducers/getters'
import { RenderSellTicketStatus } from '../../inputTableLayouts/order/OrderSellTicketTPVLayout/components/RenderSellTicketStatus'

type TableSellTicketsProps = TableComponentProps & ReduxTableSellTickets & {
    onRowClick?: OnRowClick
}

const TableSellTickets: React.FC<TableSellTicketsProps> = ({ setTableInstance, onRowClick }) => {
    const { t } = useTwinTranslation()
    const { sellTicketStatus } = useSellTicketStatusType()
    const memoEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getEmployees() || {}))), [])
    const columns: ColumnTableSchema<SellTicketModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('ticket', 'Ticket'),
            searchKey: 'SellTicket.full_assigned_number',
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('ticket', 'Ticket') }
            },
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            searchKey: 'Customer.name',
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Customer?.fiscal_name || allRowData?.Customer?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
        },
        {
            id: 'status',
            dataKey: 'status',
            searchKey: 'SellTicket.status',
            label: t('status', 'Estado'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue) => <RenderSellTicketStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: sellTicketStatus, label: t('statusTicket', 'Estado del ticket') }
            },
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'WorkingCenterId',
            searchKey: 'WorkingCenter.name',
            label: t('soldBy', 'Vendido por'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.WorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 120
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            customRender: (parameterValue, allRowData) => <CustomRenderDateHours value={parameterValue || allRowData.createdAt} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CreateEmployeeId',
            dataKey: 'CreateEmployeeId',
            label: t('madeBy', 'Realizado por'),
            hideColumnByDefault: true,
            searchKey: 'CreateEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.CreateEmployee ? allRowData?.CreateEmployee?.fullname_short : allRowData?.ProcessEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('madeBy', 'Realizado por'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            searchSingleKey: 'CreateEmployee.id'
        },
        {
            id: 'maxRefundDate',
            dataKey: 'maxRefundDate',
            label: t('maxRefundDateShort', 'Fecha máx. dev.'),
            simpleLabel: t('maxRefundDate', 'Fecha máxima de devolución'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || ''} />,
            sortable: true,
            minWidth: 165,
        },
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.sellTicketSingle, ['$id']]} name='sellTickets' columns={columns} getDataFrom='/api/app/order/tpv/sellTicket/getAllSellTicketsComplete'  onRowClick={onRowClick} setTableInstance={setTableInstance} />
    )
}

const TableSellTicketsDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxTableSellTickets = ConnectedProps<typeof tableSellTicketsConnect>
const mapTableSellTicketsConnector = (state: AllReduxPayloads) => ({ employees: state.employees })
const tableSellTicketsConnect = connect(mapTableSellTicketsConnector, TableSellTicketsDispatch)
const TableSellTicketsConnectLoading = withLoading(TableSellTickets, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])
const TableSellTicketsConnect = tableSellTicketsConnect(TableSellTicketsConnectLoading)

export default TableSellTicketsConnect